import { Button } from '@mui/material';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';

import { updateDepositInfo } from '@/api/user';

import { FormErrorLogger } from '@/components/Form/FormErrorLogger';
import { ActionBar } from '@/components/Layout/Layout.styled';

import { useAppSelector } from '@/hooks/redux';
import { useMutationWithUserData } from '@/hooks/useMutationWithUserData';
import { useTranslate } from '@/hooks/useTranslate';

import { sortBankAccountsByProductAndCurrency } from '@/pages/account-type/utils/caymanAccount';

import { selectUserData } from '@/redux/user-data/selectors';

import { AccountFormData } from '@/types/user';

import { getDataTestId } from '@/utils/browser';
import { getDepositInfoPayload } from '@/utils/user';

import { QuestionBusinessPurposeForm } from '../../components/QuestionBusinessPurposeForm';
import { QuestionProps } from './QuestionDrawer.interface';
import { QuestionForm } from './QuestionForm';

export type AccountPurposeFormData = {
  accounts: AccountFormData[];
};

export function QuestionAccountPurpose({
  onAnswered,
  ...props
}: Readonly<QuestionProps>) {
  const { t } = useTranslate();
  const { user } = useAppSelector(selectUserData);

  const bankingAccounts = useMemo(
    () => sortBankAccountsByProductAndCurrency(user?.accountTypes || []),
    [user?.accountTypes],
  );

  const {
    handleSubmit,
    control,
    formState: { isValid },
  } = useForm<AccountPurposeFormData>({
    defaultValues: {
      accounts: [],
    },
    mode: 'onChange',
  });

  const { mutate, isPending, error } = useMutationWithUserData({
    mutationFn: updateDepositInfo,
    onFinished: onAnswered,
  });

  const onSubmit = handleSubmit(async payload => {
    if (!bankingAccounts.length) return;

    const depositInfoArray = getDepositInfoPayload(
      bankingAccounts,
      payload.accounts,
    );
    mutate(depositInfoArray);
  });

  return (
    <QuestionForm
      {...props}
      isLoading={isPending}
      answerError={error}
      onSubmit={onSubmit}
    >
      {bankingAccounts.map((item, index) => (
        <QuestionBusinessPurposeForm
          account={item}
          control={control}
          index={index}
          key={item.id}
        />
      ))}
      <ActionBar>
        <Button
          {...getDataTestId(`basic-info-proceed-button`)}
          variant="outlined"
          fullWidth
          type="submit"
          disabled={!isValid || isPending}
        >
          {t('questionDrawer.monthlyDeposit.button.proceed')}
        </Button>
      </ActionBar>
      <FormErrorLogger formName="QuestionAccountPurpose" control={control} />
    </QuestionForm>
  );
}
